import { createPopper } from '@popperjs/core';
import { addDays, subDays, addMonths, subMonths, parse } from 'date-fns';

const SELECTOR = '[data-component-datepicker]';

function triggerEvent(input, name) {
  const evt = new CustomEvent(name, { bubbles: true, cancelable: true });
  input.dispatchEvent(evt);
}

function formatDateUTC(date) {
  // This concat is required to avoid dates off by one day in negative UTC timezones
  return new Date(date.concat('T00:00:00'));
}

async function loadDatepicker() {
  const { default: AirDatepicker } = await import('air-datepicker');
  const { default: locale } = await import(`air-datepicker/locale/${document.documentElement.lang || 'es'}.js`);

  return {
    AirDatepicker,
    locale,
  };
}

function mountAirDatepicker(AirDatepicker, locale) {
  return async (element) => {
    const $el = $(element);
    const $inputsWrapper = $el.find('.air-datepicker-inputs-wrapper');
    const inputEl = $el.find('.air-real-input')[0];
    const altEl = $el.find('.air-alt-input')[0];

    // CONFIG OPTIONS
    const kind = $el.data('componentDatepicker') || 'single';
    // TODO: Remove when we have multiple dateformats from backend
    // const dateFormat = $el.data('dateFormat') || locale.dateFormat;
    const dateFormat = 'dd/MM/yyyy';
    const minDate = $el.data('minDate') ? parse($el.data('minDate'), dateFormat, new Date()) : undefined;
    const maxDate = $el.data('maxDate') ? parse($el.data('maxDate'), dateFormat, new Date()) : undefined;
    const minDateId = $el.data('minDateId') || null;
    const maxDateId = $el.data('maxDateId') || null;
    const weekStart = $el.data('weekStart') || '1';
    const isInline = typeof $el.data('inline') !== 'undefined';

    const opts = {
      altField: altEl,
      altFieldDateFormat: dateFormat,
      autoClose: true,
      dateFormat,
      toggleSelected: false,
      multipleDatesSeparator: ' - ',
      inline: isInline,
      minDate,
      maxDate,
      onSelect({ date }) {
        triggerEvent(inputEl, 'change');
        // If kind is range we need two dates in the input value to trigger the change event
        if (kind !== 'range' || (kind === 'range' && inputEl.value.split(opts.multipleDatesSeparator).length > 1)) {
          triggerEvent(inputEl, 'date-selected');
        }
        if (inputEl.value !== '') {
          $el.find('.air-clear-btn').removeClass('!hidden');
        }

        if (minDateId) {
          // eslint-disable-next-line no-underscore-dangle
          const elMinDp = document.querySelector(`#${minDateId}`)._datepicker;

          elMinDp.minDate = date || minDate;
        }

        if (maxDateId) {
          // eslint-disable-next-line no-underscore-dangle
          const elMaxDp = document.querySelector(`#${maxDateId}`)._datepicker;

          elMaxDp.maxDate = date || maxDate;
        }
      },
      position({ $datepicker, $target, done }) {
        if (isInline) return;
        const popper = createPopper($target, $datepicker, {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              options: {
                padding: {
                  bottom: 24,
                },
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 12],
              },
            },
          ],
        });

        /*
        Return function which will be called when `hide()` method is triggered,
        it must necessarily call the `done()` function to complete hiding process
        */
        // eslint-disable-next-line consistent-return
        return function completeHide() {
          popper.destroy();
          done();
        };
      },
    };

    opts.locale = locale;
    opts.firstDay = parseInt(weekStart, 10);

    switch (kind) {
      case 'month-year':
        Object.assign(opts, {
          altFieldDateFormat: 'MMM yyyy',
          minView: 'months',
          selectedDates: [formatDateUTC(inputEl.value)],
          view: 'months',
        });
        break;
      case 'range':
        Object.assign(opts, {
          range: true,
          selectedDates: inputEl.value.split(' - ').map(e => formatDateUTC(e)),
        });

        // TODO: Enable when rangelinks available
        // if (typeof $el.data('rangelinks') !== 'undefined') {
        //   Object.assign(opts, {
        //     onShow() {
        //       addRangeLinks(datepicker, opts.locale);
        //     },
        //   });
        // }
        break;

      default: // Default is single date
        Object.assign(opts, {
          selectedDates: [formatDateUTC(inputEl.value)],
        });
        break;
    }

    const datepicker = new AirDatepicker(inputEl, opts);

    // Buttons / interactions

    // Add clear button
    if (kind !== 'month-year' && $el.data('clearable') !== false) {
      const $clearBtn = $('<span class="air-clear-btn">×</span>').on('click', e => {
        datepicker.clear();
        $(e.target).addClass('!hidden');
      });
      if (inputEl.value === '') {
        $clearBtn.addClass('!hidden');
      }
      $clearBtn.appendTo($inputsWrapper);
      $el.addClass('has-air-clear-btn');
    }

    $el.find('[data-prev]').on('click', () => {
      const dateUTC = formatDateUTC(inputEl.value);
      if (!Number.isNaN(dateUTC)) {
        // Check if valid date
        if (kind === 'month-year') {
          datepicker.selectDate(subMonths(dateUTC, 1));
        } else if (kind === 'single') {
          datepicker.selectDate(subDays(dateUTC, 1));
        }
        // kind === 'range' does not support `next / `prev`
      }
    });
    $el.find('[data-next]').on('click', () => {
      const dateUTC = formatDateUTC(inputEl.value);
      if (!Number.isNaN(dateUTC)) {
        // Check if valid date
        if (kind === 'month-year') {
          datepicker.selectDate(addMonths(dateUTC, 1));
        } else if (kind === 'single') {
          datepicker.selectDate(addDays(dateUTC, 1));
        }
        // kind === 'range' does not support `next / `prev`
      }
    });

    // eslint-disable-next-line no-underscore-dangle, no-param-reassign
    inputEl._datepicker = datepicker;
    return datepicker;
  };
}

$(document).on('dom-update', async (e) => {
  const datepickers = $(e.target).find(SELECTOR);

  if (!datepickers.length) return;

  const { AirDatepicker, locale } = await loadDatepicker();
  const cMountDatepicker = mountAirDatepicker(AirDatepicker, locale);

  datepickers.each((_, element) => {
    cMountDatepicker(element);
  });
});
